import React, { useState, useEffect } from 'react';
import { FiCheck, FiAlertCircle, FiLoader, FiTrash2, FiInfo } from 'react-icons/fi';
import { getTenants, onboardTenant, validateTenant, deleteTenant } from '../api';
import { useTenant } from '../components/Layout';

export default function TenantOnboarding() {
  const { refreshTenants } = useTenant();
  const [form, setForm] = useState({ tenant_name: '', tenant_id: '', client_id: '', client_secret: '', subscription_id: '', cloud_provider: 'azure' });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchTenants = () => {
    getTenants()
      .then(res => {
        const data = res.data;
        setTenants(Array.isArray(data) ? data : (data.tenants || []));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setValidationResult(null);
    setMessage(null);
  };

  const handleValidate = async () => {
    if (!form.tenant_id || !form.client_id || !form.client_secret) {
      setValidationResult({ success: false, error: 'Please fill in Tenant ID, Client ID, and Client Secret' });
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await validateTenant(form);
      setValidationResult({ success: true, data: res.data });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Validation failed - check your credentials';
      setValidationResult({ success: false, error: errorMsg });
    }
    setValidating(false);
  };

  const handleOnboard = async () => {
    if (!form.tenant_name || !form.tenant_id || !form.client_id || !form.client_secret) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    setOnboarding(true);
    setMessage(null);
    try {
      await onboardTenant(form);
      setMessage({ type: 'success', text: 'Tenant onboarded successfully! You can now use the dashboard to analyze your landing zone.' });
      setForm({ tenant_name: '', tenant_id: '', client_id: '', client_secret: '', subscription_id: '', cloud_provider: 'azure' });
      setValidationResult(null);
      fetchTenants();
      refreshTenants();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Onboarding failed';
      setMessage({ type: 'error', text: errorMsg });
    }
    setOnboarding(false);
  };

  const handleDelete = async (tenant) => {
    setDeleting(tenant.id);
    try {
      await deleteTenant(tenant.id);
      setMessage({ type: 'success', text: `Tenant '${tenant.name || tenant.tenant_name}' deleted successfully` });
      fetchTenants();
      refreshTenants();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to delete tenant' });
    }
    setDeleting(null);
    setConfirmDelete(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Tenant Onboarding</h1>
        <p>Register your cloud tenant to start analyzing landing zones</p>
      </div>

      <div className="grid-2">
        <div className="card">
          <h3 style={{ marginBottom: '1.25rem' }}>Onboard Cloud Tenant</h3>

          <div className="form-group">
            <label>Cloud Provider <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { value: 'azure', label: 'Azure', icon: '☁️', color: '#0078d4' },
                { value: 'aws', label: 'AWS', icon: '🔶', color: '#ff9900' },
                { value: 'gcp', label: 'GCP', icon: '🔵', color: '#4285f4' },
              ].map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm({ ...form, cloud_provider: p.value })}
                  style={{
                    flex: 1, padding: '0.6rem 1rem', borderRadius: 8, cursor: 'pointer',
                    border: form.cloud_provider === p.value ? `2px solid ${p.color}` : '2px solid #e5e7eb',
                    background: form.cloud_provider === p.value ? `${p.color}10` : '#fff',
                    fontWeight: form.cloud_provider === p.value ? 600 : 400,
                    fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>{p.icon}</span> {p.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', background: '#eff6ff', color: '#1e40af', fontSize: '0.85rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <FiInfo style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{
              form.cloud_provider === 'azure'
                ? 'You need an Azure AD App Registration with Reader access to your subscriptions. Provide the Tenant ID, Client ID (Application ID), and Client Secret from your app registration.'
                : form.cloud_provider === 'aws'
                ? 'You need an AWS IAM user or role with read-only access. Provide the Account ID, Access Key ID, and Secret Access Key.'
                : 'You need a GCP Service Account with Viewer role. Provide the Project ID, Client Email, and Private Key from your service account JSON.'
            }</span>
          </div>

          {message && (
            <div style={{ padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', background: message.type === 'success' ? '#d1fae5' : '#fee2e2', color: message.type === 'success' ? '#065f46' : '#991b1b', fontSize: '0.875rem', fontWeight: 500 }}>
              {message.text}
            </div>
          )}

          <div className="form-group">
            <label>Tenant Display Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input name="tenant_name" value={form.tenant_name} onChange={handleChange} placeholder={
              form.cloud_provider === 'azure' ? 'e.g., Contoso Corp Production' :
              form.cloud_provider === 'aws' ? 'e.g., MyOrg Production Account' :
              'e.g., MyOrg GCP Project'
            } />
          </div>
          <div className="form-group">
            <label>{form.cloud_provider === 'azure' ? 'Azure AD Tenant ID' : form.cloud_provider === 'aws' ? 'AWS Account ID' : 'GCP Project ID'} <span style={{ color: '#ef4444' }}>*</span></label>
            <input name="tenant_id" value={form.tenant_id} onChange={handleChange} placeholder={
              form.cloud_provider === 'azure' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' :
              form.cloud_provider === 'aws' ? '123456789012' :
              'my-gcp-project-id'
            } style={{ fontFamily: 'monospace' }} />
          </div>
          <div className="form-group">
            <label>{form.cloud_provider === 'azure' ? 'Application (Client) ID' : form.cloud_provider === 'aws' ? 'Access Key ID' : 'Service Account Client Email'} <span style={{ color: '#ef4444' }}>*</span></label>
            <input name="client_id" value={form.client_id} onChange={handleChange} placeholder={
              form.cloud_provider === 'azure' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' :
              form.cloud_provider === 'aws' ? 'AKIAIOSFODNN7EXAMPLE' :
              'svc-account@project.iam.gserviceaccount.com'
            } style={{ fontFamily: 'monospace' }} />
          </div>
          <div className="form-group">
            <label>{form.cloud_provider === 'azure' ? 'Client Secret' : form.cloud_provider === 'aws' ? 'Secret Access Key' : 'Service Account Private Key'} <span style={{ color: '#ef4444' }}>*</span></label>
            <input name="client_secret" type="password" value={form.client_secret} onChange={handleChange} placeholder={
              form.cloud_provider === 'azure' ? 'Your app registration client secret' :
              form.cloud_provider === 'aws' ? 'Your AWS secret access key' :
              'Your service account private key'
            } />
          </div>
          <div className="form-group">
            <label>{form.cloud_provider === 'azure' ? 'Subscription ID' : form.cloud_provider === 'aws' ? 'Region' : 'Region'} <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
            <input name="subscription_id" value={form.subscription_id} onChange={handleChange} placeholder={
              form.cloud_provider === 'azure' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (auto-discovers if empty)' :
              form.cloud_provider === 'aws' ? 'us-east-1 (defaults to us-east-1)' :
              'us-central1 (defaults to us-central1)'
            } style={{ fontFamily: 'monospace' }} />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleValidate} disabled={validating || !form.tenant_id || !form.client_id || !form.client_secret}>
              {validating ? <><FiLoader className="spinning" /> Validating...</> : 'Validate Credentials'}
            </button>
            <button className="btn btn-primary" onClick={handleOnboard} disabled={onboarding || !form.tenant_name || !form.tenant_id || !form.client_id || !form.client_secret}>
              {onboarding ? <><FiLoader className="spinning" /> Onboarding...</> : 'Onboard Tenant'}
            </button>
          </div>

          {validationResult && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: 6, background: validationResult.success ? '#d1fae5' : '#fee2e2', fontSize: '0.875rem' }}>
              {validationResult.success ? (
                <div>
                  <div style={{ color: '#065f46', fontWeight: 500 }}><FiCheck style={{ marginRight: 4 }} /> Credentials validated successfully</div>
                  {validationResult.data?.subscriptions && (
                    <div style={{ marginTop: '0.5rem', color: '#065f46' }}>
                      Found {validationResult.data.subscriptions.length} subscription(s):
                      <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                        {validationResult.data.subscriptions.map((s, i) => (
                          <li key={i}>{s.display_name} ({s.subscription_id})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ color: '#991b1b' }}><FiAlertCircle style={{ marginRight: 4 }} /> {validationResult.error}</span>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '1.25rem' }}>Onboarded Tenants</h3>
          {loading ? (
            <div className="loading-container"><div className="spinner" /></div>
          ) : tenants.length > 0 ? (
            <div>
              {tenants.map((t, i) => (
                <div key={i} style={{ padding: '1rem', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: '0.75rem', background: '#f9fafb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '1rem' }}>{t.name || t.tenant_name || 'N/A'}</span>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                        background: (t.cloud_provider || 'azure') === 'azure' ? '#dbeafe' : (t.cloud_provider || 'azure') === 'aws' ? '#fef3c7' : '#dbeafe',
                        color: (t.cloud_provider || 'azure') === 'azure' ? '#1e40af' : (t.cloud_provider || 'azure') === 'aws' ? '#92400e' : '#1d4ed8'
                      }}>
                        {t.cloud_provider || 'azure'}
                      </span>
                      <span className={`badge badge-${t.status === 'active' ? 'success' : t.status === 'error' ? 'danger' : 'info'}`}>
                        {t.status || 'active'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'monospace' }}>
                    ID: {t.tenant_id || t.id || 'N/A'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#6b7280' }}>
                      <span>{t.resource_count || 0} resources</span>
                      <span>{t.subscription_count || 0} subscriptions</span>
                    </div>
                    {confirmDelete === t.id ? (
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 500 }}>Delete all data?</span>
                        <button
                          onClick={() => handleDelete(t)}
                          disabled={deleting === t.id}
                          style={{
                            padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                            background: '#ef4444', color: '#fff', fontSize: '0.75rem', fontWeight: 600
                          }}
                        >
                          {deleting === t.id ? 'Deleting...' : 'Yes'}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          style={{
                            padding: '3px 10px', borderRadius: 4, border: '1px solid #d1d5db', cursor: 'pointer',
                            background: '#fff', color: '#374151', fontSize: '0.75rem', fontWeight: 500
                          }}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(t.id)}
                        title="Delete tenant"
                        style={{
                          padding: '4px 8px', borderRadius: 4, border: '1px solid #e5e7eb', cursor: 'pointer',
                          background: '#fff', color: '#6b7280', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; e.target.style.borderColor = '#fca5a5'; }}
                        onMouseLeave={(e) => { e.target.style.background = '#fff'; e.target.style.color = '#6b7280'; e.target.style.borderColor = '#e5e7eb'; }}
                      >
                        <FiTrash2 size={13} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#d1d5db' }}>
                <FiAlertCircle />
              </div>
              <p style={{ color: '#9ca3af', marginBottom: '0.5rem' }}>No tenants onboarded yet</p>
              <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Select a cloud provider and fill in your credentials on the left to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
